import React, { useEffect, useState } from "react";
import AppSettings from "../../../Settings/AppSettings";
import ServiceWrapper from "../../../Services/ServiceWrapper";
import AuthManager from "../../../Common/AuthManager";
import Utils from "../../../Common/Utils";
import { toast } from "Components/DesignSystem/Toast/useToast";
import RefreshIcon from "../Builder/Components/RefreshIcon";
import ProductViewHeader from "./ProductView/ProductViewHeader";
import ProductViewSkeleton from "./ProductView/ProductViewSkeleton";
import Attributes from "./ProductView/Attributes";
import MoreDetails from "./ProductView/MoreDetails";
import AccordionBody from "Components/Common/components/Accordion/AccordionBody"
import AccordionHeader from "Components/Common/components/Accordion/AccordionHeader"
import AccordionLayout from "Components/Common/components/Accordion/AccordionLayout"
import DeltaTable from "Components/Common/DeltaTable"
import Card from "Components/DesignSystem/Card/Card";
import Button from "Components/DesignSystem/Button/Button";
import Typography from "Components/DesignSystem/Typography/Typography";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import ErrorState from "Components/DesignSystem/ErrorState/ErrorState";
import Tooltip from "Components/DesignSystem/Tooltip/Tooltip";
import { Copy, InfoCircle, ImageIcon } from "Components/DesignSystem/Iconography/Icons";
import ImagePlaceholder from "Components/DesignSystem/ImagePlaceholder/ImagePlaceholder";


const ProductView = ({ productid }) => {

  const [productDetails, setProductDetails] = useState(null);
  const [productImage, setProductImage] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [compatibleProduct, setCompatibleProduct] = useState(null);
  // § 77: a LOCAL asset — the legacy placeholder was hotlinked from vectorstock.com.
  /*
   * URLs that failed to load. One set, shared by the main slot and the thumbnails,
   * keyed on the URL itself rather than a boolean — so clicking a thumbnail whose
   * image works clears the placeholder by construction, with no reset effect and no
   * stale flag left over from the previous image.
   *
   * The empty state used to be `assests/img/placeholder.svg`, which was not vector
   * art: a 32x32 SVG shell around an embedded, malformed 275x183 PNG, masked into a
   * circle by `<rect rx="16">`. Stretched into this 196px slot it rendered blurred and
   * round, weighed 123 KB, and baked its light colours in so it could never theme.
   * (It had replaced a hotlink to vectorstock.com — okf/products/e2e.md.) The DS
   * `ImagePlaceholder` (§ 191) is the ground that stands where an image should be.
   */
  const [failedImageUrls, setFailedImageUrls] = useState(() => new Set());
  const markImageFailed = (url) =>
    setFailedImageUrls((prev) => (prev.has(url) ? prev : new Set(prev).add(url)));
  const canShowImage = (url) => Boolean(url) && !failedImageUrls.has(url);
  const [loadCompatible, setLoadCompatible] = useState(true);

  const [Loading, setLoading] = useState({
    status: "",
    message: "",
  });

  let api_base_url = AppSettings.GMS_BACKEND_API_URL;

  async function load_Product_details_by_id() {
    setProductDetails(null);
    setShowBuilder(false);
    setLoading({
      status: "Loading",
      message: "Loading Product Details",
    });

    let res = await ServiceWrapper.doGet(
      `${api_base_url}product/details/${productid} `,
      null,
      null,
      AuthManager.getGmsToken()
    );

    if (!res.errorfound) {
      if (res.data.done === true) {
        if (res?.data?.data === null) {
          toast.error("Data error");
          setProductDetails(null);
          setShowBuilder(true);
          return;
        }
        setProductDetails(res.data.data);
        if (res?.data?.data?.product_image_detail !== "") {
          let final = JSON.parse(
            res.data.data.product_image_detail.replace(/'/g, '"')
          );
          if (
            final &&
            final.length > 0 &&
            final[0].JPEG_IMAGES &&
            final[0].JPEG_IMAGES.default_image
          ) {
            let images = [];
            images.push(final[0].JPEG_IMAGES.default_image);
            images = images.concat(final[0].JPEG_IMAGES.images);
            setProductImage(final[0].JPEG_IMAGES.default_image);
            setProductImages(images);
          }
        }
        setLoading({
          status: "Success",
          message: undefined,
        });
        setShowBuilder(true);
      } else {
        setLoading({
          status: "Error",
          message: res.data.message,
        });
        setShowBuilder(true);
      }
    } else {
      setShowBuilder(true);
      setLoading({
        status: "Error",
        message: "Error Loading Product Details",
      });
    }
  }

  const [clipText, setClipText] = useState(null);

  function handle_clipbord(text) {
    navigator.clipboard.writeText(text).then(() => setClipText(text));
    setTimeout(() => {
      setClipText(null);
    }, 2000);
  }

  useEffect(() => {
    if(!productid) return

    load_Product_details_by_id();
    get_compactible_products(productid);
  }, [productid]);



  const get_compactible_products = async (productId) => {
    setLoadCompatible(true);
    let res = await ServiceWrapper.doGet(
      `${api_base_url}product/${productId}/compatibleproducts`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    if (!res.errorfound) {
      if (res.data.done) {
        setTimeout(() => {
          get_file_processing_status(res.data.jobid);
        }, 2000);
      } else {
        toast.error(res.data.message);
      }
    } else {
      toast.error("Error Loading Compatible Products");
    }
  };

  async function get_file_processing_status(jobid) {
    let ret = await ServiceWrapper.doGet(
      api_base_url + "job/status/" + jobid,
      null,
      null,
      AuthManager.getGmsToken(),
      true
    );

    if (!ret.errorfound) {
      if (ret.data.processing) {
        setTimeout(() => {
          get_file_processing_status(jobid);
        }, 2000);
      } else {
        setCompatibleProduct([])
        if (ret.data.error) {
          toast.error(ret.data.status);
        }
        if (ret.data.data) {
          setCompatibleProduct(ret.data.data);
          setLoadCompatible(false);
        }
      }
    } else {
      toast.error("An Error occurred");
      setCompatibleProduct([])
    }
  }

  const handleCompatibleProductsRefresh= (e) => {
    e.preventDefault()
    get_compactible_products(productid)
  }

  const staticHeadersForCompatibleProducts = [{name: "Name/Id", column: "name", options: { 
      width: "400px", 
      sticky: [true]
    }}
  ]

  return (
    <div id="product-detail-page">

      {!showBuilder && Loading.status === "Loading" && <ProductViewSkeleton/>}

      {/* § 78: an actual error used to fall into the same bare "No Products found" div —
          the stored message was never rendered. The two states are distinct surfaces now. */}
      {showBuilder && !productDetails && Loading.status === "Error" && (
        <ErrorState
          id="product-view-error"
          data-testid="product-view-error"
          title="We couldn't load this product"
          description={Loading.message}
        />
      )}

      {showBuilder && !productDetails && Loading.status !== "Error" && (
        <EmptyState
          id="product-detail-empty-state"
          data-testid="product-detail-empty-state"
          size="sm"
          title="No Products found"
        />
      )}

      {showBuilder && productDetails && <>
      <ProductViewHeader productName={productDetails?.name ?? "---"} productid = {productid} categoryid = {productDetails?.category_id}/>

      <Card id="product-detail-info-card" data-testid="product-detail-info-card" className="tw-my-3">
        <div className="tw-flex tw-flex-wrap tw-gap-4">
          <div className="tw-flex-shrink-0">
            {canShowImage(productImage) ? (
              <div className="tw-rounded-srf tw-w-[196px] tw-h-[196px] tw-bg-surface-sunken">
                {/* `onError` is new: a product whose image URL fails (a 404, an expired
                    CDN link) used to show the browser's own broken-image glyph here,
                    because only the thumbnails had a fallback. */}
                <img
                  id="product-detail-main-image"
                  className="tw-p-2.5 tw-w-[196px] tw-h-[196px]"
                  src={productImage}
                  alt="Product"
                  loading="lazy"
                  onError={() => markImageFailed(productImage)}
                />
              </div>
            ) : (
              /* The id moves with the slot, so `#product-detail-main-image` still finds the
                 main image region whether or not there is an image to show. It is now a
                 `role="img"` named "No image available", not an `<img src>`. */
              <ImagePlaceholder
                id="product-detail-main-image"
                data-testid="product-detail-main-image-placeholder"
                width="196px"
                height="196px"
              />
            )}
            <div id="product-detail-image-thumbnails" className="tw-flex tw-w-48 tw-overflow-x-auto">
              {productImages && productImages?.length > 0 ? productImages?.map((imageUrl, index) => (
                /* § 77: a real button — the legacy `div role="button"` was keyboard-unreachable,
                   and its testid was DUPLICATED across every thumbnail; the per-index id string
                   is the testid now. */
                <button
                  type="button"
                  data-testid={`product-detail-thumbnail-${index}`}
                  aria-label={`Select image ${index + 1}`}
                  key={index + imageUrl}
                  id={`product-detail-thumbnail-${index}`}
                  className="tw-rounded tw-mt-1 tw-w-[35px] tw-h-[35px] tw-bg-surface-sunken tw-flex-shrink-0 tw-mx-0.5 tw-p-0 tw-border-0 tw-cursor-pointer"
                  onClick={() => {setProductImage(imageUrl);}}
                >
                  {canShowImage(imageUrl) ? (
                    <img
                      className="tw-w-[35px] tw-h-[35px]"
                      src={imageUrl}
                      alt=""
                      loading="lazy"
                      onError={() => markImageFailed(imageUrl)}
                    />
                  ) : (
                    /* Not `ImagePlaceholder`: its 28px icon plus `tw-p-3` needs 52px, and
                       this thumbnail is 35px. The button already supplies the sunken
                       ground, so only the glyph is needed. */
                    <span
                      className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-text-ink-tertiary"
                      data-testid={`product-detail-thumbnail-${index}-placeholder`}
                    >
                      <ImageIcon size={16} aria-hidden="true" />
                    </span>
                  )}
                </button>
              )) : null}
            </div>
          </div>
          <div className="tw-flex-1 tw-min-w-0">
            <Typography className="tw-text-ink" variant="heading-2" as="h2" id="product-detail-name">{productDetails?.name?.toString() ?? "---"}</Typography>
            <Typography variant="body-primary" as="p" id="product-detail-description" className="tw-mt-2.5">{productDetails?.description ?? "---"}</Typography>
            <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1">
              <Typography variant="sub-title-label" as="span">UID:</Typography>
              <Typography variant="caption-metadata" as="span" id="product-detail-uid" className="tw-text-ink-secondary">{productDetails?.id ?? "---"}</Typography>
              {/* § 77: a real button (was a bare <i onClick>). The transient inline
                  confirmation is the legacy behavior, kept as-is. */}
              <button
                type="button"
                data-testid="products-productview-i-2"
                id="product-detail-btn-copy-uid"
                aria-label="Copy UID"
                className="tw-ml-1 tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-text-accent"
                onClick={() => handle_clipbord(productDetails.id)}
              >
                <Copy />
              </button>
              {clipText !== null && (
                <Typography variant="caption-metadata" as="span" className="tw-ml-1 tw-text-tone-success-ink">UID Copied</Typography>
              )}
            </div>
            <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1">
              <Typography variant="sub-title-label" as="span">Model:</Typography>
              <Typography variant="caption-metadata" as="span" id="product-detail-model" className="tw-text-ink-secondary">{productDetails?.model_number ?? "---"}</Typography>
            </div>
            <Attributes attr_value={productDetails?.attr_vals} />
            <MoreDetails
              min_ad_price={productDetails?.min_ad_price?.toString() ?? "---"}
              upc={productDetails?.upc?.toString() ?? "---"}
              normalized_min_ad_price={productDetails?.normalized_min_ad_price?.toString() ??"---"}
              product_lifecycle_status={productDetails?.product_lifecycle_status}
              />
          </div>
        </div>
      </Card>

      <div id="product-detail-compatible-header" className="tw-justify-between tw-flex tw-items-center tw-gap-2 tw-rounded-srf tw-bg-surface tw-box-border tw-w-auto tw-h-12 tw-border tw-border-edge tw-p-2.5">
        <div className="tw-flex tw-items-center tw-gap-2">
          <Typography className="tw-text-ink" variant="title-header" as="h3">Compatible products</Typography>
          <Tooltip content="This section displays products that work seamlessly with the current item based on established compatibility rules and specifications." usePortal>
            <Button variant="subtle" size="sm" aria-label="About compatible products">
              <InfoCircle />
            </Button>
          </Tooltip>
        </div>
        <Button
          variant="subtle"
          size="sm"
          aria-label="Refresh"
          id="product-detail-btn-refresh-compatible"
          data-testid="product-refresh-compatible-btn"
          onClick={handleCompatibleProductsRefresh}
        >
          <RefreshIcon/>
        </Button>
      </div>
      {
        loadCompatible &&
        <div aria-busy="true" data-testid="compatible-products-loading" className="tw-mt-2">
          <span role="status" className="tw-sr-only">Loading compatible products</span>
          <div aria-hidden="true" className="tw-space-y-2">
            <Skeleton variant="text" lines={5} />
          </div>
        </div>
      }
      <div id="product-detail-compatible-products" className="tw-pb-5">
        {
          !loadCompatible &&
          compatibleProduct?.categories &&
          compatibleProduct.categories.length > 0 &&
          compatibleProduct.categories.map((category, index) => (
            <div
            key={index}
            id={`product-detail-compatible-category-${index}`}
            >
                <AccordionLayout>
                    {(provided) => {
                    const attrs = Utils.generateHeadersFromArray(category?.columns ?? [], category?.custom_columns ?? [])
                      
                    return (
                    <>
                          <div className={`tw-font-semibold tw-border tw-border-edge tw-px-2`}>
                            <AccordionHeader {...provided.getHeaderProps()} data-testid={`compatible-category-accordion-${category.category_name}`}>
                                    {/* Explicit ink: the bare <p> inherited a light-only literal and
                                        vanished on the themed card in dark (reported 2026-09-07). */}
                                    <p className={`tw-text-ink`}>{category.category_name}</p>
                            </AccordionHeader>
                          </div>

                        <AccordionBody {...provided.getBodyProps()}>
                          <div>
                            <DeltaTable
                              columns = {category?.columns ? [...staticHeadersForCompatibleProducts ,...attrs] : null}
                              data = {category?.products}
                              options={{
                                disableSort: true,
                                disableHeaderControls: true,
                                disableRowOptions: true,
                                striped: true
                              }}
                              />
                          </div>
                        </AccordionBody>
                    </>
                    )}}
                </AccordionLayout>
            </div>

          ))
        }
      </div>
      </>}
    </div>
  );
};
export default ProductView;